/**
 * 火山引擎增长分析（DataRangers）埋点工具
 * 基于官方 collectEvent API 封装，提供统一的埋点接口
 */

class TrackingService {
  constructor() {
    this.initialized = false
    this.config = {
      appId: process.env.VUE_APP_DATARANGERS_APP_ID || 20012071, // 应用ID
      channelDomain: process.env.VUE_APP_DATARANGERS_CHANNEL_DOMAIN || 'https://gator.volces.com', // 数据上报地址
      enableLog: process.env.VUE_APP_BUILD_ENV === 'dev', // 开发环境开启日志
      debug: process.env.VUE_APP_DATARANGERS_DEBUG === 'true' || process.env.VUE_APP_BUILD_ENV === 'dev' // 调试模式
    }
  }

  /**
   * 等待 SDK 加载完成
   * @returns {Promise}
   */
  waitForSDK() {
    return new Promise((resolve) => {
      if (window.collectEvent && typeof window.collectEvent === 'function') {
        resolve()
      } else {
        // 轮询检查 SDK 是否加载完成
        const checkInterval = setInterval(() => {
          if (window.collectEvent && typeof window.collectEvent === 'function') {
            clearInterval(checkInterval)
            resolve()
          }
        }, 50)

        // 10秒超时
        setTimeout(() => {
          clearInterval(checkInterval)
          if (this.config.enableLog) {
            console.warn('DataRangers SDK 加载超时')
          }
          resolve()
        }, 10000)
      }
    })
  }

  /**
   * 初始化 SDK
   * @param {Object} options 配置选项
   * @param {number} options.appId 应用ID（必填）
   * @param {string} options.channelDomain 数据上报地址（可选）
   * @param {boolean} options.disableSdkMonitor 是否禁用SDK监控（可选，默认true）
   * @param {boolean} options.debug 是否开启调试模式（可选）
   * @param {string} options.userUniqueId 用户唯一标识（可选）
   * @param {Object} options.userProperties 用户属性（可选）
   */
  async init(options = {}) {
    if (this.initialized) {
      if (this.config.enableLog) {
        console.warn('DataRangers SDK 已经初始化，跳过重复初始化')
      }
      return
    }

    // 等待 SDK 加载完成
    await this.waitForSDK()

    if (!window.collectEvent) {
      console.error('DataRangers SDK 未加载，请检查 script 标签是否正确引入')
      return
    }

    // 判断是否开启调试模式
    const isDebug = options.debug !== undefined ? options.debug : this.config.debug

    const config = {
      app_id: options.appId || this.config.appId,
      channel_domain: options.channelDomain || this.config.channelDomain,
      disable_sdk_monitor: options.disableSdkMonitor !== undefined ? options.disableSdkMonitor : true
    }

    // 如果开启调试模式，添加调试相关配置
    if (isDebug) {
      config.debug = true
      config.log = true
      // 在控制台输出调试信息
      console.log('%c[DataRangers Debug]', 'color: #4CAF50; font-weight: bold;', '调试模式已开启')
      console.log('%c[DataRangers Config]', 'color: #2196F3; font-weight: bold;', config)
    }

    if (!config.app_id) {
      console.error('DataRangers SDK 初始化失败：app_id 为必填项')
      return
    }

    try {
      // 初始化 SDK
      window.collectEvent('init', config)
      
      // 启动 SDK
      window.collectEvent('start')
      
      this.initialized = true
      this.config.debug = isDebug

      // 设置用户信息（如果提供）
      if (options.userUniqueId) {
        this.setUserUniqueId(options.userUniqueId)
      }
      if (options.userProperties) {
        this.setUserProperties(options.userProperties)
      }

      if (this.config.enableLog || isDebug) {
        console.log('%c[DataRangers]', 'color: #4CAF50; font-weight: bold;', 'SDK 初始化成功', config)
      }
    } catch (error) {
      console.error('DataRangers SDK 初始化失败:', error)
    }
  }

  /**
   * 设置用户唯一标识
   * @param {string} userId 用户ID
   */
  setUserUniqueId(userId) {
    if (!window.collectEvent) {
      if (this.config.enableLog || this.config.debug) {
        console.warn('%c[DataRangers]', 'color: #FF9800; font-weight: bold;', 'SDK 未加载，无法设置用户ID')
      }
      return
    }
    try {
      window.collectEvent('setUserUniqueId', userId)
      if (this.config.enableLog || this.config.debug) {
        console.log('%c[DataRangers]', 'color: #2196F3; font-weight: bold;', '设置用户ID:', userId)
      }
    } catch (error) {
      console.error('%c[DataRangers Error]', 'color: #F44336; font-weight: bold;', '设置用户ID失败:', error)
    }
  }

  /**
   * 设置用户属性
   * @param {Object} properties 用户属性对象
   */
  setUserProperties(properties) {
    if (!window.collectEvent) {
      if (this.config.enableLog || this.config.debug) {
        console.warn('%c[DataRangers]', 'color: #FF9800; font-weight: bold;', 'SDK 未加载，无法设置用户属性')
      }
      return
    }
    try {
      window.collectEvent('setUserProperties', properties)
      if (this.config.enableLog || this.config.debug) {
        console.log('%c[DataRangers]', 'color: #2196F3; font-weight: bold;', '设置用户属性:', properties)
      }
    } catch (error) {
      console.error('%c[DataRangers Error]', 'color: #F44336; font-weight: bold;', '设置用户属性失败:', error)
    }
  }

  /**
   * 上报事件
   * @param {string} eventName 事件名称
   * @param {Object} properties 事件属性（可选）
   */
  track(eventName, properties = {}) {
    if (!window.collectEvent) {
      if (this.config.enableLog || this.config.debug) {
        console.warn('%c[DataRangers]', 'color: #FF9800; font-weight: bold;', 'SDK 未加载，事件未上报:', eventName, properties)
      }
      return
    }

    try {
      window.collectEvent('track', eventName, properties)
      if (this.config.enableLog || this.config.debug) {
        console.log('%c[DataRangers Track]', 'color: #2196F3; font-weight: bold;', `事件: ${eventName}`, properties)
        if (this.config.debug) {
          console.log('%c[DataRangers Debug]', 'color: #9C27B0; font-weight: bold;', '事件已上报，可在控制台查看网络请求')
        }
      }
    } catch (error) {
      console.error('%c[DataRangers Error]', 'color: #F44336; font-weight: bold;', '上报事件失败:', error)
    }
  }

  /**
   * 页面浏览事件
   * @param {string} pageName 页面名称
   * @param {Object} properties 页面属性（可选）
   */
  trackPageView(pageName, properties = {}) {
    const pageProperties = {
      page_name: pageName,
      page_path: window.location.pathname,
      page_url: window.location.href,
      ...properties
    }
    this.track('$pageview', pageProperties)
  }

  /**
   * 点击事件
   * @param {string} elementName 元素名称
   * @param {Object} properties 事件属性（可选）
   */
  trackClick(elementName, properties = {}) {
    const clickProperties = {
      element_name: elementName,
      ...properties
    }
    this.track('$click', clickProperties)
  }

  /**
   * 登录事件
   * @param {string} userId 用户ID
   * @param {Object} properties 登录属性（可选）
   */
  trackLogin(userId, properties = {}) {
    this.setUserUniqueId(userId)
    const loginProperties = {
      user_id: userId,
      login_method: properties.loginMethod || 'unknown',
      ...properties
    }
    this.track('login', loginProperties)
  }

  /**
   * 登出事件
   */
  trackLogout() {
    this.track('logout', {})
    // 清除用户ID
    this.setUserUniqueId('')
  }

  /**
   * 搜索事件
   * @param {string} keyword 搜索关键词
   * @param {Object} properties 搜索属性（可选）
   */
  trackSearch(keyword, properties = {}) {
    const searchProperties = {
      keyword: keyword,
      ...properties
    }
    this.track('search', searchProperties)
  }

  /**
   * 分享事件
   * @param {string} shareType 分享类型
   * @param {string} shareContent 分享内容
   * @param {Object} properties 分享属性（可选）
   */
  trackShare(shareType, shareContent, properties = {}) {
    const shareProperties = {
      share_type: shareType,
      share_content: shareContent,
      ...properties
    }
    this.track('share', shareProperties)
  }

  // ==================== C端候选人埋点 ====================

  /**
   * AI面试入口曝光
   * @param {Object} options 曝光信息
   * @param {string} options.page 页面：'job_detail' | 'apply_success'
   * @param {string|number} options.positionId 职位ID（必填）
   */
  trackAiInterviewEntranceExpose(options = {}) {
    if (!options.positionId) {
      console.warn('AI面试入口曝光埋点：positionId 为必填项')
    }
    const properties = {
      page: options.page || 'job_detail',
      position_id: String(options.positionId || ''),
      ...options.extraProperties
    }
    this.track('ai_interview_entrance_expose', properties)
  }

  /**
   * AI面试开始
   * @param {Object} options 面试信息
   * @param {string} options.interviewId 面试ID（必填）
   * @param {string|number} options.positionId 职位ID（必填）
   */
  trackAiInterviewStart(options = {}) {
    if (!options.interviewId || !options.positionId) {
      console.warn('AI面试开始埋点：interviewId 和 positionId 为必填项')
    }
    const properties = {
      interview_id: String(options.interviewId || ''),
      position_id: String(options.positionId || ''),
      ...options.extraProperties
    }
    this.track('ai_interview_start', properties)
  }

  /**
   * AI面试答题提交
   * @param {Object} options 答题信息
   * @param {string} options.interviewId 面试ID（必填）
   */
  trackAiInterviewAnswerSubmit(options = {}) {
    if (!options.interviewId) {
      console.warn('AI面试答题提交埋点：interviewId 为必填项')
    }
    const properties = {
      interview_id: String(options.interviewId || ''),
      ...options.extraProperties
    }
    this.track('ai_interview_answer_submit', properties)
  }

  /**
   * AI面试结束
   * @param {Object} options 结束信息
   * @param {string} options.interviewId 面试ID（必填）
   * @param {string} options.finishType 结束类型：'normal' | 'timeout' | 'user_quit'
   * @param {number} options.totalDuration 面试总时长（毫秒，必填）
   * @param {number} options.completeRate 完成率（0-1，必填）
   */
  trackAiInterviewFinish(options = {}) {
    if (!options.interviewId || !options.finishType || options.totalDuration === undefined || options.completeRate === undefined) {
      console.warn('AI面试结束埋点：interviewId、finishType、totalDuration、completeRate 为必填项')
    }
    const properties = {
      interview_id: String(options.interviewId || ''),
      finish_type: options.finishType || 'normal',
      total_duration: options.totalDuration || 0,
      complete_rate: options.completeRate !== undefined ? options.completeRate : 0,
      ...options.extraProperties
    }
    this.track('ai_interview_finish', properties)
  }

  /**
   * AI面试结果页曝光
   * @param {Object} options 结果页信息
   * @param {string} options.interviewId 面试ID（必填）
   * @param {number} options.score AI综合评分（0-100，必填）
   * @param {string|Array} options.dimensionList 维度得分列表（JSON字符串或数组）
   * @param {boolean|number} options.shareClick 是否点击分享（0/1或true/false）
   */
  trackAiInterviewResultExpose(options = {}) {
    if (!options.interviewId || options.score === undefined) {
      console.warn('AI面试结果页曝光埋点：interviewId 和 score 为必填项')
    }
    const properties = {
      interview_id: String(options.interviewId || ''),
      score: options.score !== undefined ? options.score : 0,
      dimension_list: Array.isArray(options.dimensionList) 
        ? JSON.stringify(options.dimensionList) 
        : (options.dimensionList || ''),
      share_click: options.shareClick !== undefined 
        ? (typeof options.shareClick === 'boolean' ? (options.shareClick ? 1 : 0) : options.shareClick)
        : 0,
      ...options.extraProperties
    }
    this.track('ai_interview_result_expose', properties)
  }

  /**
   * 注册成功事件
   * @param {string} userId 用户ID
   * @param {Object} options 注册信息
   * @param {string} options.registerChannel 注册渠道：'wechat' | 'mobile'
   * @param {number} options.registerDuration 注册时长（毫秒）
   * @param {Object} options.extraProperties 额外属性
   */
  trackRegisterSuccess(userId, options = {}) {
    if (!userId || !options.registerChannel) {
      console.warn('注册成功埋点：userId 和 registerChannel 为必填项')
    }
    const properties = {
      user_id: String(userId || ''),
      register_channel: options.registerChannel || 'mobile',
      register_duration: options.registerDuration || 0,
      ...options.extraProperties
    }
    this.setUserUniqueId(userId)
    this.track('register_success', properties)
  }

  /**
   * 简历创建/完善事件
   * @param {string} userId 用户ID
   * @param {Object} options 简历信息
   * @param {number} options.improveFieldCnt 完善字段数（必填）
   * @param {number} options.aiResumeOptimizeUsed AI简历优化使用次数
   * @param {number} options.completionRate 完成率（0-1，必填）
   * @param {Object} options.extraProperties 额外属性
   */
  trackResumeCreateOrImprove(userId, options = {}) {
    if (!userId || options.improveFieldCnt === undefined || options.completionRate === undefined) {
      console.warn('简历创建/完善埋点：userId、improveFieldCnt、completionRate 为必填项')
    }
    const properties = {
      user_id: String(userId || ''),
      improve_field_cnt: options.improveFieldCnt !== undefined ? options.improveFieldCnt : 0,
      ai_resume_optimize_used: options.aiResumeOptimizeUsed || 0,
      completion_rate: options.completionRate !== undefined ? options.completionRate : 0,
      ...options.extraProperties
    }
    this.track('resume_create_or_improve', properties)
  }

  /**
   * 职位搜索/浏览事件
   * @param {string} userId 用户ID
   * @param {Object} options 搜索/浏览信息
   * @param {string} options.searchKeyword 搜索关键词（当scene=search时必填）
   * @param {string|number} options.positionId 职位ID（必填）
   * @param {number} options.browseDuration 浏览时长（毫秒）
   * @param {number} options.aiRecommendClickCnt AI推荐点击数
   * @param {Object} options.extraProperties 额外属性
   */
  trackJobSearchOrBrowse(userId, options = {}) {
    if (!userId || !options.positionId) {
      console.warn('职位搜索/浏览埋点：userId 和 positionId 为必填项')
    }
    const properties = {
      user_id: String(userId || ''),
      search_keyword: options.searchKeyword || '',
      position_id: String(options.positionId || ''),
      browse_duration: options.browseDuration || 0,
      ai_recommend_click_cnt: options.aiRecommendClickCnt || 0,
      ...options.extraProperties
    }
    this.track('job_search_or_browse', properties)
  }

  /**
   * 投递职位事件
   * @param {string} userId 用户ID
   * @param {Object} options 投递信息
   * @param {string|number} options.positionId 职位ID（必填）
   * @param {string|number} options.companyId 企业ID（必填）
   * @param {string} options.deliverMethod 投递方式：'active' | 'ai_match'
   * @param {string} options.deliverResult 投递结果：'success' | 'fail' | 'duplicate'
   * @param {Object} options.extraProperties 额外属性
   */
  trackJobDeliver(userId, options = {}) {
    if (!userId || !options.positionId || !options.companyId || !options.deliverMethod || !options.deliverResult) {
      console.warn('投递职位埋点：userId、positionId、companyId、deliverMethod、deliverResult 为必填项')
    }
    const properties = {
      user_id: String(userId || ''),
      position_id: String(options.positionId || ''),
      company_id: String(options.companyId || ''),
      deliver_method: options.deliverMethod || 'active',
      deliver_result: options.deliverResult || 'success',
      ...options.extraProperties
    }
    this.track('job_deliver', properties)
  }

  /**
   * 用户登录事件
   * @param {string} userId 用户ID（登录成功时必填，失败时可为空）
   * @param {Object} options 登录信息
   * @param {string} options.loginChannel 登录渠道：'wechat' | 'mobile' | 'email' | 'apple'
   * @param {string} options.loginType 登录类型：'verify_code' | 'password' | 'third_party'
   * @param {string} options.loginResult 登录结果：'success' | 'fail'
   * @param {string} options.failReason 失败原因（仅当loginResult=fail时有效）
   * @param {number} options.loginDuration 登录耗时（毫秒）
   * @param {Object} options.extraProperties 额外属性
   */
  trackUserLogin(userId, options = {}) {
    if (!options.loginChannel || !options.loginType || !options.loginResult) {
      console.warn('用户登录埋点：loginChannel、loginType、loginResult 为必填项')
    }
    const properties = {
      user_id: userId ? String(userId) : '',
      login_channel: options.loginChannel || 'mobile',
      login_type: options.loginType || 'verify_code',
      login_result: options.loginResult || 'success',
      fail_reason: options.failReason || '',
      login_duration: options.loginDuration || 0,
      ...options.extraProperties
    }
    if (userId && options.loginResult === 'success') {
      this.setUserUniqueId(userId)
    }
    // 添加调试日志
    console.log('%c[用户登录埋点]', 'color: #FF5722; font-weight: bold;', '调用 trackUserLogin', {
      userId,
      options,
      properties,
      eventName: 'user_login'
    })
    this.track('user_login', properties)
  }

  /**
   * 推荐岗位曝光
   * @param {string} userId 用户ID
   * @param {Object} options 曝光信息
   * @param {string|number} options.positionId 职位ID（必填）
   * @param {string|number} options.companyId 企业ID（必填）
   * @param {string} options.recommendSource 推荐来源：'ai_match' | 'hot_recommend' | 'personalized' | 'position_similar'
   * @param {string} options.exposeScene 曝光场景：'homepage' | 'resume_match' | 'search_result' | 'message_push'
   * @param {boolean|number} options.hasRecommendVideo 是否含推荐视频（0/1或true/false）
   * @param {number} options.exposePosition 曝光位置（推荐列表中的排序位置）
   * @param {Object} options.extraProperties 额外属性
   */
  trackJobRecommendExpose(userId, options = {}) {
    if (!userId || !options.positionId || !options.companyId || !options.recommendSource || !options.exposeScene) {
      console.warn('推荐岗位曝光埋点：userId、positionId、companyId、recommendSource、exposeScene 为必填项')
    }
    const properties = {
      user_id: String(userId || ''),
      position_id: String(options.positionId || ''),
      company_id: String(options.companyId || ''),
      recommend_source: options.recommendSource || 'ai_match',
      expose_scene: options.exposeScene || 'homepage',
      has_recommend_video: options.hasRecommendVideo !== undefined 
        ? (typeof options.hasRecommendVideo === 'boolean' ? (options.hasRecommendVideo ? 1 : 0) : options.hasRecommendVideo)
        : 0,
      expose_position: options.exposePosition || 0,
      ...options.extraProperties
    }
    this.track('job_recommend_expose', properties)
  }

  /**
   * 推荐岗位/视频点击
   * @param {string} userId 用户ID
   * @param {Object} options 点击信息
   * @param {string|number} options.positionId 职位ID（必填）
   * @param {string|number} options.companyId 企业ID（必填）
   * @param {string} options.recommendSource 推荐来源：'ai_match' | 'hot_recommend' | 'personalized' | 'position_similar'
   * @param {string} options.clickType 点击类型：'position_card' | 'recommend_video'
   * @param {string} options.videoId 视频ID（当clickType=recommend_video时必填）
   * @param {number} options.clickPosition 点击位置（推荐列表中的排序位置）
   * @param {number} options.exposeToClickDuration 曝光到点击耗时（毫秒）
   * @param {Object} options.extraProperties 额外属性
   */
  trackJobRecommendClick(userId, options = {}) {
    if (!userId || !options.positionId || !options.companyId || !options.recommendSource || !options.clickType) {
      console.warn('推荐岗位/视频点击埋点：userId、positionId、companyId、recommendSource、clickType 为必填项')
    }
    if (options.clickType === 'recommend_video' && !options.videoId) {
      console.warn('推荐岗位/视频点击埋点：当clickType=recommend_video时，videoId为必填项')
    }
    const properties = {
      user_id: String(userId || ''),
      position_id: String(options.positionId || ''),
      company_id: String(options.companyId || ''),
      recommend_source: options.recommendSource || 'ai_match',
      click_type: options.clickType || 'position_card',
      video_id: options.videoId || '',
      click_position: options.clickPosition || 0,
      expose_to_click_duration: options.exposeToClickDuration || 0,
      ...options.extraProperties
    }
    this.track('job_recommend_click', properties)
  }

  /**
   * AI面试付费转化
   * @param {string} userId 用户ID
   * @param {Object} options 付费信息
   * @param {string} options.interviewId 面试ID（必填）
   * @param {string|number} options.positionId 职位ID（必填）
   * @param {string} options.payEntrance 付费入口：'result_page_btn' | 'pop_up' | 'message_notify' | 'other'
   * @param {string} options.payStatus 付费状态：'success' | 'fail' | 'cancel'
   * @param {number} options.payAmount 付费金额（分，必填）
   * @param {string} options.payMethod 支付方式：'wechat_pay' | 'alipay' | 'apple_pay' | 'other'
   * @param {string} options.failReason 失败原因（仅当payStatus=fail时有效）
   * @param {number} options.payDuration 决策耗时（毫秒）
   * @param {Object} options.extraProperties 额外属性
   */
  trackAiInterviewPay(userId, options = {}) {
    if (!userId || !options.interviewId || !options.positionId || !options.payEntrance || !options.payStatus || options.payAmount === undefined) {
      console.warn('AI面试付费转化埋点：userId、interviewId、positionId、payEntrance、payStatus、payAmount 为必填项')
    }
    const properties = {
      user_id: String(userId || ''),
      interview_id: String(options.interviewId || ''),
      position_id: String(options.positionId || ''),
      pay_entrance: options.payEntrance || 'result_page_btn',
      pay_status: options.payStatus || 'success',
      pay_amount: options.payAmount || 0,
      pay_method: options.payMethod || '',
      fail_reason: options.failReason || '',
      pay_duration: options.payDuration || 0,
      ...options.extraProperties
    }
    this.track('ai_interview_pay', properties)
  }

  /**
   * 职业规划付费转化
   * @param {string} userId 用户ID
   * @param {Object} options 付费信息
   * @param {string} options.careerPlanId 职业规划ID（必填）
   * @param {string} options.payEntrance 付费入口：'homepage_module' | 'resume_improve' | 'ai_interview_result' | 'search' | 'other'
   * @param {string} options.payStatus 付费状态：'success' | 'fail' | 'cancel'
   * @param {number} options.payAmount 付费金额（分，必填）
   * @param {string} options.planType 规划类型：'entry_level' | 'mid_level' | 'senior_level' | 'custom'
   * @param {string} options.payMethod 支付方式：'wechat_pay' | 'alipay' | 'apple_pay' | 'other'
   * @param {string} options.failReason 失败原因（仅当payStatus=fail时有效）
   * @param {number} options.payDuration 决策耗时（毫秒）
   * @param {Object} options.extraProperties 额外属性
   */
  trackCareerPlanPay(userId, options = {}) {
    if (!userId || !options.careerPlanId || !options.payEntrance || !options.payStatus || options.payAmount === undefined) {
      console.warn('职业规划付费转化埋点：userId、careerPlanId、payEntrance、payStatus、payAmount 为必填项')
    }
    const properties = {
      user_id: String(userId || ''),
      career_plan_id: String(options.careerPlanId || ''),
      pay_entrance: options.payEntrance || 'homepage_module',
      pay_status: options.payStatus || 'success',
      pay_amount: options.payAmount || 0,
      plan_type: options.planType || '',
      pay_method: options.payMethod || '',
      fail_reason: options.failReason || '',
      pay_duration: options.payDuration || 0,
      ...options.extraProperties
    }
    this.track('career_plan_pay', properties)
  }
}

// 创建单例
const trackingService = new TrackingService()

export default trackingService

